import { useContext, useState } from "react";
import { useEffect } from "react";
import { Button } from "react-bootstrap";

import ConfirmationPopup from "../../../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../../../shared/components/spinner/spinner";
import Tooltip from "../../../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../../../shared/context/auth-context";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import {
  getAllData,
  getData,
  postSingle,
} from "../../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ControlledTable from "../../../controlled-table/controlled-table";
import {
  IColumn,
  ISortedBy,
  ITableRowData,
} from "../../../data-table/data-table";
import LinkedAlertcasesModal from "./linked-alertcases-modal";

export interface IRelatedEventWidget {
  id: string;
  type: "relatedEvent";
  modalType: "datamodelRelatedEvent";
  alertcaseRowId?: string;
  label: string;
  selectedColumns: IColumn[];
  datamodelId: string;
  modalData?: {
    // Extra information the modal needs to edit this widget
    [index: string]: string;
  };
  relatedEntityTypeId?: string;
  relatedEntityId?: string;
  alertcaseId?: string;
}

interface IRelatedEventWidgetProps {
  widget: IRelatedEventWidget;
  previewMode?: boolean;
  alertcaseRowId?: string;
  relatedEntityTypeId?: string;
  relatedEntityId?: string;
  alertcaseId?: string;
}

const RelatedEventWidget = ({
  widget,
  previewMode,
  alertcaseRowId,
  relatedEntityTypeId,
  relatedEntityId,
  alertcaseId,
}: IRelatedEventWidgetProps) => {
  const limit = 50;
  const authCtx = useContext(AuthContext);
  const [data, setData] = useState<ITableRowData[]>([]);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({
    column: "",
    direction: "desc",
  });
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [isUserReadOnly, setIsUserReadOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showConsolidatePopup, setShowConsolidatePopup] = useState(false);
  const [managerConfig, setManagerConfig] = useState<any>({}); // eslint-disable-line
  const [showLinkedAlertcasesModal, setShowLinkedAlertcasesModal] =
    useState(false);
  const [rowId, setRowId] = useState<string>("");
  const [primaryDatetimeDBColumnName, setPrimaryDatetimeDBColumnName] =
    useState<string>("");

  const {
    sendRequest: getTableData,
    status: getTableDataStatus,
    data: getTableDataResult,
    error: getTableDataError,
  } = useHttp(postSingle, false, authCtx.token, "datamodelAssociation", "manager");
  const {
    sendRequest: postLinkData,
    status: postLinkDataStatus,
    error: postLinkDataError,
  } = useHttp(postSingle, false, authCtx.token, "linkTableUpdate", "manager");
  const {
    sendRequest: getManagerConfig,
    status: getManagerConfigStatus,
    data: getManagerConfigData
  } = useHttpGet(getData, true, authCtx.token, undefined, "manager");
  const {
    sendRequest: getEventConsolidation,
    status: getEventConsolidationStatus,
    data: getEventConsolidationData,
    error: getEventConsolidationError,
  } = useHttp(
    getAllData,
    false,
    authCtx.token,
    "eventConsolidationInfo",
    "manager"
  );
  const {
    sendRequest: postConsolidateData,
    status: postConsolidateDataStatus,
    error: postConsolidateDataError
  } = useHttp(
    postSingle,
    false,
    authCtx.token,
    "consolidateByEventIds",
    "manager"
  );

  const handleModalClick = (row: ITableRowData) => {
    setRowId(row.id);
    setShowLinkedAlertcasesModal(true);
  };

  useEffect(() => {
    const roles = authCtx.selectedTenant.roles || [];
    setIsUserReadOnly(roles.length === 1 && roles[0] === "Read");
  }, [authCtx.selectedTenant]);

  useEffect(() => {
    if (widget.datamodelId) {
      getEventConsolidation(`event_consolidation/${widget.datamodelId}`);
    }
  }, [widget.datamodelId]);

  useEffect(() => {
    if (
      getEventConsolidationStatus === "completed" &&
      !getEventConsolidationError &&
      getEventConsolidationData
    ) {
      setPrimaryDatetimeDBColumnName(
        getEventConsolidationData?.["primary_datetime_column_db_name"]
      );
    }
  }, [getEventConsolidationStatus]);

  useEffect(() => {
    if (previewMode) {
      const dummyData: ITableRowData[] = [];
      for (let i = 0; i < widget.selectedColumns.length; i++) {
        dummyData.push(
          widget.selectedColumns.reduce(
            (acc, curr) => {
              acc[curr.key] = "...";
              return acc;
            },
            { id: i.toString() } as ITableRowData
          )
        );
      }
      setData(dummyData);
      return;
    }
    if (!relatedEntityTypeId || !relatedEntityId || !primaryDatetimeDBColumnName) {
      return;
    }

    if (!fullyLoaded) {
      let querystring = `offset=${paginationOffset*limit}&limit=${limit}`;
      if (sortedByCol.column) {
        querystring += `&order_by=${sortedByCol.column} ${sortedByCol.direction}`;
      }

      getTableData(
        `datamodel_association?${querystring}`,
        {
          alertcase_id: alertcaseId,
          datamodel_id: widget.datamodelId,
          datamodel_type: "event",
          columns: widget.selectedColumns?.map((column) => column.key) || [],
          include_alerted_info: true
        },
        undefined,
        "none"
      );
    }

    
  }, [sortedByCol, relatedEntityTypeId, relatedEntityId, primaryDatetimeDBColumnName, paginationOffset]);

  useEffect(() => {
    if (
      getTableDataStatus === "completed" &&
      !getTableDataError &&
      getTableDataResult &&
      alertcaseId
    ) {
      setData([...data, ...getTableDataResult.rows]);
      if (getTableDataResult.rows.length < limit) {
        setFullyLoaded(true);
      }
    }
  }, [getTableDataStatus, alertcaseId]);

  const handleSaveClicked = () => {
    postLinkData(`update_link_table`, {
      update_type: "alert_events",
      sygno_ids: selectedIds ? selectedIds.map((id) => id) : [],
      event_id: widget.datamodelId,
      alertcase_row_id: alertcaseRowId,
      alertcase_id: alertcaseId
    });
    setShowConfirmationPopup(false);
  };

  const handleConsolidateClicked = () => {
    postConsolidateData(`consolidate_events/${widget.datamodelId}`, {
      events: selectedIds.map((id: string) => ({ sygno_id: id })),
    });
    setShowConsolidatePopup(false);
  };

  useEffect(() => {
    if (getManagerConfigStatus === "completed" && getManagerConfigData) {
      setManagerConfig(getManagerConfigData);
    }
  }, [getManagerConfigStatus]);

  useEffect(() => {
    if (
      (postLinkDataStatus === "completed" && !postLinkDataError) ||
      (postConsolidateDataStatus === "completed" && !postConsolidateDataError)
    ) {
      setPaginationOffset(0);
      setFullyLoaded(false);
      setData([]);
      let querystring = `offset=0&limit=${limit}`;
      if (sortedByCol.column) {
        querystring += `&order_by=${sortedByCol.column} ${sortedByCol.direction}`;
      }
      getTableData(
        `datamodel_association?${querystring}`,
      {
        alertcase_id: alertcaseId,
        datamodel_id: widget.datamodelId,
        datamodel_type: "event",
        columns: widget.selectedColumns?.map((column) => column.key) || [],
        include_alerted_info: true
      },
      undefined,
      "none"
    );
    }
  }, [postLinkDataStatus, postConsolidateDataStatus, postLinkDataError]);

  useEffect(() => {
    getManagerConfig("configuration");
  }, []);

  return (
    <div className="relatedEventWidget">
      {validateLoadingStatusNew([getTableDataStatus, getEventConsolidationStatus, getManagerConfigStatus, postLinkDataStatus, postConsolidateDataStatus]) && <Spinner />}
      <ControlledTable
        markedRows={data.filter((row) => row.sygno_is_alerted).map((row) => ({ id: row.id, mark: "highlighted" as const }))}
        readOnly={isUserReadOnly}
        allowSelectAll={false}
        style="compact"
        title={`Related events (${widget.label})`}
        data={data}
        selectionMode="multi"
        columns={[
          ...widget.selectedColumns,
          {
            key: "sygno_suspicious",
            displayName: managerConfig.suspicious_definition || "Suspicious",
            dataType: { type: "text" },
          },
          {
            key: "modal",
            displayName: "Alert info",
            dataType: {
              type: "action",
              buttonText: `ℹ️`,
              shouldShow: (row: ITableRowData) => row.sygno_other_alerts_count > 0,
              buttonStyle: "none",
              tooltipText:
                "Click to see the related alertcases with this event",
            },
          },
        ]}
        onSorted={(sortedByCol: ISortedBy) => {
          setSortedByCol(sortedByCol);
          setData([]);
          setPaginationOffset(0);
          setFullyLoaded(false);
        }}
        onScroll={() => setPaginationOffset((prev) => prev + 1)}
        onRowClick={noop}
        onSelectionChange={setSelectedIds}
        onSaveClicked={() => setShowConfirmationPopup(true)}
        customSaveButtonText="Add to alertcase"
        customSaveButtonTooltip="Add to alertcase"
        disableSaveButton={previewMode || isUserReadOnly || selectedIds.length === 0}
        extraButtons={
          <Tooltip content={"Consolidate"} direction="top" delay={350}>
            <Button
              onClick={() => setShowConsolidatePopup(true)}
              className="popup-action"
              disabled={ previewMode || isUserReadOnly || selectedIds.length === 0}
            >
              Consolidate
            </Button>
          </Tooltip>
        }
        showFalse={true}
        onActionClick={(action_key: string, rowId: string) =>
          handleModalClick({ id: rowId } as ITableRowData)
        }
        rowHeight="small"
      />
      <ConfirmationPopup
        confirmButtonText={"Save"}
        text={"Are you sure you want to mark the selected events as alerted?"}
        show={showConfirmationPopup}
        handlePopupClosed={() => setShowConfirmationPopup(false)}
        handleSaveClick={handleSaveClicked}
      />

      <ConfirmationPopup
        confirmButtonText={"Consolidate"}
        text={"Are you sure you want to consolidate the selected events?"}
        show={showConsolidatePopup}
        handlePopupClosed={() => setShowConsolidatePopup(false)}
        handleSaveClick={handleConsolidateClicked}
      />

      {showLinkedAlertcasesModal && (
        <LinkedAlertcasesModal
          show={showLinkedAlertcasesModal}
          handlePopupClosed={() => setShowLinkedAlertcasesModal(false)}
          alertcaseRowId={alertcaseRowId}
          selectedId={rowId}
          eventId={widget.datamodelId}
        />
      )}
    </div>
  );
};

export default RelatedEventWidget;
